"use client";
import { useCallback, useEffect, useRef, useState } from "react";

interface UseKeyboardNavigationParams {
  totalResults: number;
  hasNoResults: boolean;
  onSelect: (index: number) => void;
  onClose: () => void;
  onNoResultsAction?: () => void;
  isOpen: boolean;
}

export function useKeyboardNavigation({
  totalResults,
  hasNoResults,
  onSelect,
  onClose,
  onNoResultsAction,
  isOpen
}: UseKeyboardNavigationParams) {
  const [activeIndex, setActiveIndex] = useState(0);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(0);
  }, [totalResults]);

  // Scroll active item into view
  useEffect(() => {
    const activeElement = itemRefs.current.get(activeIndex);
    if (activeElement) {
      activeElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest"
      });
    }
  }, [activeIndex]);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveIndex(0);
    }
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          if (hasNoResults) return;
          setActiveIndex((prev) => Math.min(prev + 1, totalResults - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          if (hasNoResults) return;
          setActiveIndex((prev) => Math.max(prev - 1, 0));
          break;
        case "Enter":
          e.preventDefault();
          if (hasNoResults) {
            onNoResultsAction?.();
          } else {
            onSelect(activeIndex);
          }
          break;
        case "Escape":
          e.preventDefault();
          onClose();
          break;
      }
    },
    [
      hasNoResults,
      totalResults,
      activeIndex,
      onSelect,
      onClose,
      onNoResultsAction
    ]
  );

  const registerItemRef = useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      if (el) {
        itemRefs.current.set(index, el);
      } else {
        itemRefs.current.delete(index);
      }
    },
    []
  );

  return {
    activeIndex,
    handleKeyDown,
    registerItemRef
  };
}
